/**
 * Procedural sound engine — all sounds synthesized via Web Audio API.
 * Call soundSystem.unlock() on the first user gesture (handled automatically
 * on the first play() call).
 */
class SoundSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private alertInterval: ReturnType<typeof setInterval> | null = null;
  private engineOsc: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;

  // ── Initialisation ──

  private getCtx(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.55;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  private play(fn: (ctx: AudioContext, out: AudioNode) => void) {
    try {
      const ctx = this.getCtx();
      fn(ctx, this.master!);
    } catch {}
  }

  // ── Weapons ──

  phaser() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(800, t);
      osc.frequency.exponentialRampToValueAtTime(1900, t + 0.7);
      osc.frequency.exponentialRampToValueAtTime(950, t + 1.3);

      const lfo = ctx.createOscillator();
      lfo.frequency.value = 28;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 100;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);

      const filt = ctx.createBiquadFilter();
      filt.type = "bandpass";
      filt.frequency.value = 1300;
      filt.Q.value = 2.5;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.28, t + 0.05);
      gain.gain.setValueAtTime(0.28, t + 1.0);
      gain.gain.linearRampToValueAtTime(0, t + 1.5);

      osc.connect(filt); filt.connect(gain); gain.connect(out);
      lfo.start(t); osc.start(t);
      lfo.stop(t + 1.5); osc.stop(t + 1.5);
    });
  }

  torpedo() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;

      // Thump
      const thump = ctx.createOscillator();
      thump.type = "sine";
      thump.frequency.setValueAtTime(110, t);
      thump.frequency.exponentialRampToValueAtTime(35, t + 0.35);
      const thumpGain = ctx.createGain();
      thumpGain.gain.setValueAtTime(0.5, t);
      thumpGain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      thump.connect(thumpGain); thumpGain.connect(out);
      thump.start(t); thump.stop(t + 0.5);

      // High whine
      const whine = ctx.createOscillator();
      whine.type = "sawtooth";
      whine.frequency.setValueAtTime(700, t);
      whine.frequency.exponentialRampToValueAtTime(200, t + 0.45);
      const whineGain = ctx.createGain();
      whineGain.gain.setValueAtTime(0.14, t);
      whineGain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass"; hp.frequency.value = 400;
      whine.connect(hp); hp.connect(whineGain); whineGain.connect(out);
      whine.start(t); whine.stop(t + 0.5);
    });
  }

  // ── Defenses ──

  shieldsUp() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(240, t);
      osc.frequency.exponentialRampToValueAtTime(720, t + 0.45);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
      osc.connect(gain); gain.connect(out);
      osc.start(t); osc.stop(t + 0.55);
    });
  }

  shieldsDown() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(720, t);
      osc.frequency.exponentialRampToValueAtTime(200, t + 0.5);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
      osc.connect(gain); gain.connect(out);
      osc.start(t); osc.stop(t + 0.55);
    });
  }

  shieldImpact() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.45);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * 0.7;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass"; bp.frequency.value = 900; bp.Q.value = 5;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.4, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
      src.connect(bp); bp.connect(gain); gain.connect(out);
      src.start(t);

      // Metallic ring
      const ring = ctx.createOscillator();
      ring.frequency.value = 460;
      const rg = ctx.createGain();
      rg.gain.setValueAtTime(0.18, t);
      rg.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
      ring.connect(rg); rg.connect(out);
      ring.start(t); ring.stop(t + 0.55);
    });
  }

  // ── Events ──

  explosion(large = false) {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      const dur = large ? 1.6 : 0.8;
      const len = Math.floor(ctx.sampleRate * dur);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) {
        const env = Math.pow(1 - i / len, 0.4);
        d[i] = (Math.random() * 2 - 1) * env;
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass"; lp.frequency.value = large ? 700 : 400;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(large ? 0.75 : 0.45, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
      src.connect(lp); lp.connect(gain); gain.connect(out);
      src.start(t);
    });
  }

  warp() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;

      // Swoosh noise
      const len = Math.floor(ctx.sampleRate * 1.6);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass"; bp.Q.value = 8;
      bp.frequency.setValueAtTime(4500, t);
      bp.frequency.exponentialRampToValueAtTime(180, t + 1.2);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.45, t + 0.1);
      gain.gain.setValueAtTime(0.45, t + 0.9);
      gain.gain.linearRampToValueAtTime(0, t + 1.6);
      src.connect(bp); bp.connect(gain); gain.connect(out);
      src.start(t);

      // Deep rumble
      const rumble = ctx.createOscillator();
      rumble.type = "sine"; rumble.frequency.value = 55;
      rumble.frequency.linearRampToValueAtTime(28, t + 1.6);
      const rg = ctx.createGain();
      rg.gain.setValueAtTime(0.35, t);
      rg.gain.exponentialRampToValueAtTime(0.001, t + 1.6);
      rumble.connect(rg); rg.connect(out);
      rumble.start(t); rumble.stop(t + 1.6);
    });
  }

  // ── Operations ──

  transporter() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      // Shimmering high-frequency sparkle
      const osc1 = ctx.createOscillator();
      osc1.type = "sine";
      osc1.frequency.setValueAtTime(2400, t);
      osc1.frequency.linearRampToValueAtTime(1200, t + 2.5);
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 18;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 400;
      lfo.connect(lfoGain);
      lfoGain.connect(osc1.frequency);
      const gain1 = ctx.createGain();
      gain1.gain.setValueAtTime(0, t);
      gain1.gain.linearRampToValueAtTime(0.15, t + 0.3);
      gain1.gain.setValueAtTime(0.15, t + 2.0);
      gain1.gain.linearRampToValueAtTime(0, t + 2.8);
      osc1.connect(gain1); gain1.connect(out);
      lfo.start(t); osc1.start(t);
      lfo.stop(t + 2.8); osc1.stop(t + 2.8);

      // Low hum underneath
      const hum = ctx.createOscillator();
      hum.type = "sine";
      hum.frequency.value = 80;
      const humGain = ctx.createGain();
      humGain.gain.setValueAtTime(0, t);
      humGain.gain.linearRampToValueAtTime(0.1, t + 0.5);
      humGain.gain.setValueAtTime(0.1, t + 2.0);
      humGain.gain.linearRampToValueAtTime(0, t + 2.8);
      hum.connect(humGain); humGain.connect(out);
      hum.start(t); hum.stop(t + 2.8);
    });
  }

  cloak() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      // Descending shimmer
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(1800, t);
      osc.frequency.exponentialRampToValueAtTime(120, t + 1.4);
      const filt = ctx.createBiquadFilter();
      filt.type = "bandpass";
      filt.frequency.value = 600;
      filt.Q.value = 3;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.2, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 1.5);
      osc.connect(filt); filt.connect(gain); gain.connect(out);
      osc.start(t); osc.stop(t + 1.5);
    });
  }

  decloak() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      // Ascending shimmer (reverse of cloak)
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(120, t);
      osc.frequency.exponentialRampToValueAtTime(1800, t + 1.2);
      const filt = ctx.createBiquadFilter();
      filt.type = "bandpass";
      filt.frequency.value = 600;
      filt.Q.value = 3;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.2, t + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 1.4);
      osc.connect(filt); filt.connect(gain); gain.connect(out);
      osc.start(t); osc.stop(t + 1.5);
    });
  }

  incomingHail() {
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      // Two-tone comm chime
      for (let i = 0; i < 3; i++) {
        const offset = i * 0.6;
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(523, t + offset);
        osc.frequency.setValueAtTime(659, t + offset + 0.15);
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, t + offset);
        gain.gain.linearRampToValueAtTime(0.22, t + offset + 0.02);
        gain.gain.setValueAtTime(0.22, t + offset + 0.25);
        gain.gain.linearRampToValueAtTime(0, t + offset + 0.35);
        osc.connect(gain); gain.connect(out);
        osc.start(t + offset); osc.stop(t + offset + 0.4);
      }
    });
  }

  // ── Alert states ──

  redAlert() {
    this.stopAlert();
    let beat = 0;
    const tick = () => {
      this.play((ctx, out) => {
        const t = ctx.currentTime;
        const freq = beat % 2 === 0 ? 880 : 622;
        const osc = ctx.createOscillator();
        osc.type = "square"; osc.frequency.value = freq;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.22, t + 0.02);
        gain.gain.setValueAtTime(0.22, t + 0.22);
        gain.gain.linearRampToValueAtTime(0, t + 0.32);
        osc.connect(gain); gain.connect(out);
        osc.start(t); osc.stop(t + 0.35);
        beat++;
      });
    };
    tick(); // immediate
    this.alertInterval = setInterval(tick, 440);
  }

  yellowAlert() {
    this.stopAlert();
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      for (let i = 0; i < 2; i++) {
        const offset = i * 0.38;
        const osc = ctx.createOscillator();
        osc.type = "square";
        osc.frequency.value = i % 2 === 0 ? 622 : 880;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, t + offset);
        gain.gain.linearRampToValueAtTime(0.18, t + offset + 0.02);
        gain.gain.setValueAtTime(0.18, t + offset + 0.2);
        gain.gain.linearRampToValueAtTime(0, t + offset + 0.32);
        osc.connect(gain); gain.connect(out);
        osc.start(t + offset); osc.stop(t + offset + 0.35);
      }
    });
  }

  standDown() {
    this.stopAlert();
    this.play((ctx, out) => {
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(660, t);
      osc.frequency.exponentialRampToValueAtTime(330, t + 0.55);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.2, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.65);
      osc.connect(gain); gain.connect(out);
      osc.start(t); osc.stop(t + 0.65);
    });
  }

  stopAlert() {
    if (this.alertInterval !== null) {
      clearInterval(this.alertInterval);
      this.alertInterval = null;
    }
  }

  // ── Continuous engine hum ──

  setEngineHum(speedUnits: number) {
    try {
      const ctx = this.getCtx();
      if (speedUnits <= 0) {
        if (this.engineGain) {
          this.engineGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
        }
        return;
      }
      if (!this.engineOsc) {
        this.engineOsc   = ctx.createOscillator();
        this.engineOsc.type = "sawtooth";
        this.engineFilter = ctx.createBiquadFilter();
        this.engineFilter.type = "lowpass";
        this.engineFilter.frequency.value = 180;
        this.engineGain  = ctx.createGain();
        this.engineGain.gain.value = 0;
        this.engineOsc.connect(this.engineFilter);
        this.engineFilter.connect(this.engineGain);
        this.engineGain.connect(this.master!);
        this.engineOsc.start();
      }
      const freq = 38 + speedUnits * 7;
      const vol  = Math.min(0.07 + speedUnits * 0.012, 0.14);
      const t    = ctx.currentTime;
      this.engineOsc.frequency.linearRampToValueAtTime(freq, t + 0.6);
      this.engineGain!.gain.linearRampToValueAtTime(vol, t + 0.6);
    } catch {}
  }
}

export const soundSystem = new SoundSystem();
